import React, { useState } from 'react';
import SharedHeaderHomeowner from './SharedHeaderHomeowner';

interface FAQProps {
  onNavigate: (screen: string) => void;
}

const FAQ: React.FC<FAQProps> = ({ onNavigate }) => {
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());
  const [openQuestions, setOpenQuestions] = useState<Set<string>>(new Set());

  const FAQ_CATEGORIES = [
    {
      title: 'About CoreBids',
      faqs: [
        {
          q: 'What is CoreBids?',
          a: 'CoreBids is a next-generation platform connecting homeowners and contractors for roofing and exterior projects. We streamline the bidding, compliance, and project management process for both sides.'
        },
        {
          q: 'When will CoreBids launch?',
          a: 'We are currently in private beta testing and expect to launch soon. Join the waitlist for early access & updates!'
        },
        {
          q: 'Who can join the waitlist?',
          a: 'Contractors and industry professionals interested in early access, feedback, & partnership opportunities.'
        },
        {
          q: 'Can I use the platform on mobile devices?',
          a: 'Absolutely! Our platform is fully responsive and accessible via smartphones and tablets for on-the-go management.'
        }
      ]
    },
    {
      title: 'Getting Started',
      faqs: [
        {
          q: 'How does CoreBids work?',
          a: 'CoreBids makes getting roofing and exterior work done simple: (1) Enter your address and project details—we use remote measurements to create an accurate project scope. (2) Receive competitive bids from vetted local contractors who review your project and submit their pricing. (3) Compare bids side-by-side with clear pricing, timelines, and warranties. (4) Choose the contractor that fits your needs and move forward with confidence. All communication and payments are handled securely through the platform.'
        },
        {
          q: 'How do I create a contractor profile?',
          a: 'Contractors who signup for the waitlist will be able to create a profile with an email link.Signing up is quick—just provide your business info, licenses, and insurance details to get verified.'
        },
        {
          q: 'Is there a fee to join CoreBids?',
          a: 'For our founding users we offer free basic profiles. Options for paid plans that unlock premium leads and features will be available soon.'
        },
        {
          q: 'Does the platform verify contractors?',
          a: 'Yes, we conduct license and insurance checks, background verification, and compliance reviews to ensure quality.'
        }
      ]
    },
    {
      title: 'Bidding & Leads',
      faqs: [
        {
          q: 'How does the bidding process work?',
          a: 'Homeowners post projects with measurements. You are then able to search through those projects that are posted and submit bids directly through the platform. You control your pricing and timelines.'
        },
        {
          q: 'Are the leads exclusive to me?',
          a: 'Some leads are exclusive, while others may be shared with select contractors based on project type and location.'
        },
        {
          q: 'What kind of projects are posted on the platform?',
          a: 'For now, just roofing. To come, projects will range from roofing and siding to windows, gutters, and other exterior construction services.'
        }
      ]
    },
    {
      title: 'Payments & Project Management',
      faqs: [
        {
          q: 'How do I get paid?',
          a: 'Payments are handled securely through the platform with milestone releases to ensure timely and safe transactions.'
        },
        {
          q: 'Can I communicate directly with homeowners?',
          a: 'Yes, the platform supports real-time messaging to discuss project details and updates.'
        },
        {
          q: 'What happens if a project is delayed or canceled?',
          a: 'The platform provides tools for tracking progress and managing changes, including dispute resolution support if needed.'
        }
      ]
    }
  ];

  const toggleCategory = (categoryTitle: string) => {
    const newOpenCategories = new Set(openCategories);
    if (newOpenCategories.has(categoryTitle)) {
      newOpenCategories.delete(categoryTitle);
    } else {
      newOpenCategories.add(categoryTitle);
    }
    setOpenCategories(newOpenCategories);
  };

  const toggleQuestion = (questionKey: string) => {
    const newOpenQuestions = new Set(openQuestions);
    if (newOpenQuestions.has(questionKey)) {
      newOpenQuestions.delete(questionKey);
    } else {
      newOpenQuestions.add(questionKey);
    }
    setOpenQuestions(newOpenQuestions);
  };

  // CB:BEGIN auto-expand-how-it-works
  // Auto-expand "How It Works" FAQ if coming from a link
  React.useEffect(() => {
    if (window.location.hash === '#how-it-works') {
      const gettingStartedCategory = FAQ_CATEGORIES.find(cat => cat.title === 'Getting Started');
      if (gettingStartedCategory) {
        setOpenCategories(new Set(['Getting Started']));
        setTimeout(() => {
          setOpenQuestions(new Set(['Getting Started-How does CoreBids work?']));
          const el = document.getElementById('how-it-works');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
  }, []);
  // CB:END auto-expand-how-it-works

  return (
    <div className="container">
      <SharedHeaderHomeowner onNavigate={onNavigate} showContractorButton={true} />
      <div className="content" style={{ maxWidth: 1200, margin: '0 auto', padding: '20px' }}>
        <h2 style={{ fontSize: 32, fontWeight: 800, marginBottom: 32, color: '#1e293b', textAlign: 'center' }}>Frequently Asked Questions</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {FAQ_CATEGORIES.map((category, categoryIndex) => (
          <div key={categoryIndex} style={{ 
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', 
            borderRadius: 16, 
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
          }}>
            {/* Category Header */}
            <div 
              style={{ 
                padding: '20px 24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'linear-gradient(135deg, #123c3b 0%, #0d2928 100%)',
                color: 'white',
                fontWeight: 700,
                fontSize: 18,
                transition: 'background 0.2s ease'
              }}
              onClick={() => toggleCategory(category.title)}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #0d2928 0%, #0a1f1e 100%)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #123c3b 0%, #0d2928 100%)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {category.title}
              </div>
              <div style={{ 
                transition: 'all 0.3s ease',
                fontSize: 20,
                fontWeight: 700,
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {openCategories.has(category.title) ? '−' : '+'}
              </div>
            </div>
            
            {/* Category Questions */}
            {openCategories.has(category.title) && (
              <div style={{ padding: '0 24px 24px 24px' }}>
                {category.faqs.map((faq, faqIndex) => {
                  const isHowItWorks = faq.q === 'How does CoreBids work?';
                  return (
                  <div key={faqIndex} id={isHowItWorks ? 'how-it-works' : undefined} style={{ marginTop: 16, scrollMarginTop: '80px' }}>
                    <div 
                      style={{ 
                        cursor: 'pointer',
                        padding: '16px 20px',
                        background: 'white',
                        borderRadius: 12,
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => toggleQuestion(`${category.title}-${faq.q}`)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f8fafc';
                        e.currentTarget.style.borderColor = '#123c3b';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }}
                    >
                      <div style={{ 
                        fontWeight: 600, 
                        fontSize: 16, 
                        color: '#1e293b',
                        flex: 1,
                        textAlign: 'left'
                      }}>
                        {faq.q}
                      </div>
                      <div style={{ 
                        transition: 'all 0.3s ease',
                        color: '#123c3b',
                        fontSize: 18,
                        fontWeight: 700,
                        width: 18,
                        height: 18,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {openQuestions.has(`${category.title}-${faq.q}`) ? '−' : '+'}
                      </div>
                    </div>
                    
                    {/* Answer Dropdown */}
                    {openQuestions.has(`${category.title}-${faq.q}`) && (
                      <div style={{ 
                        padding: '16px 20px',
                        background: '#f8fafc',
                        marginTop: 8,
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        animation: 'slideDown 0.3s ease'
                      }}>
                        <div style={{ 
                          color: '#374151', 
                          fontSize: 15, 
                          fontWeight: 500, 
                          lineHeight: 1.6 
                        }}>
                          {faq.a}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div style={{ textAlign: 'center', marginTop: '32px' }}>
        <button 
          onClick={() => onNavigate('landing')}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: 600,
            color: '#ffffff',
            backgroundColor: '#123c3b',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#0d2928';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#123c3b';
          }}
        >
          Back to Home
        </button>
      </div>
      </div>
  </div>
);
};

export default FAQ; 